import { useEffect, useState } from "react";
import { fetchWeather, geocode, Weather } from "@/lib/weather";

/** A Date that updates every second. */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export interface WeatherConfig {
  showWeather: boolean;
  latitude?: number;
  longitude?: number;
  locationName?: string;
  tempUnit: "F" | "C";
}

// Module-level cache: the player only mounts a small window of slides, so a
// clock slide re-mounts on every playlist loop. Without this each loop
// re-geocoded and re-fetched the weather — thousands of needless API calls a
// day on a 24/7 kiosk, and a blank weather row while each fetch ran.
const WEATHER_TTL_MS = 15 * 60 * 1000;
const weatherCache = new Map<string, { at: number; weather: Weather | null }>();

async function cachedWeather(cfg: WeatherConfig): Promise<Weather | null> {
  const key = `${cfg.latitude}|${cfg.longitude}|${cfg.locationName}|${cfg.tempUnit}`;
  const hit = weatherCache.get(key);
  if (hit && Date.now() - hit.at < WEATHER_TTL_MS) return hit.weather;
  try {
    let lat = cfg.latitude;
    let lon = cfg.longitude;
    if ((lat == null || lon == null) && cfg.locationName) {
      const g = await geocode(cfg.locationName);
      if (g) {
        lat = g.latitude;
        lon = g.longitude;
      }
    }
    if (lat == null || lon == null) return null;
    const w = await fetchWeather(lat, lon, cfg.tempUnit);
    if (w) weatherCache.set(key, { at: Date.now(), weather: w });
    return w;
  } catch {
    return hit?.weather ?? null; // keep showing the last reading on a blip
  }
}

/** Current weather for the config; cached module-wide, refreshed every 15 min. */
export function useWeather(cfg: WeatherConfig): Weather | null {
  const [weather, setWeather] = useState<Weather | null>(null);
  useEffect(() => {
    if (!cfg.showWeather) {
      setWeather(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const w = await cachedWeather(cfg);
      if (!cancelled && w) setWeather(w);
    };
    void load();
    const timer = setInterval(load, WEATHER_TTL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.showWeather, cfg.latitude, cfg.longitude, cfg.locationName, cfg.tempUnit]);
  return weather;
}
